/**
 * Cmarkdownparagraph Component
 * Paragraph component for markdown rendering
 */
'use client';
import type { ReactNode, CSSProperties } from 'react';
import { Ctext } from '../Ctext';
import { TextPreset } from '@/constants/componentPresets';
import { FontWeight } from '@/theme/typography';
import { adjustWeightForHebrew } from '../Cmarkdown/markdown-hebrew';
// No paragraph margin-bottom: the block owns no vertical rhythm of its own, so
// every Cmarkdownparagraph instance in the system sits flush and the surrounding
// container (markdown block spacing, chat bubble gap) decides the spacing.
export interface CmarkdownparagraphProps {
    children: ReactNode;
    className?: string;
    style?: CSSProperties;
    textSize?: number;
    textWeight?: FontWeight;
    textAlign?: 'left' | 'center' | 'right';
    textStyle?: CSSProperties;
    /** Hebrew-led line: render right-to-left at a lighter weight. */
    hebrew?: boolean;
}
const RTL_BLOCK_STYLE: CSSProperties = { direction: 'rtl' };
const HEBREW_TEXT_ALIGN = 'right';
export function Cmarkdownparagraph({ children, className, style, textSize, textWeight, textAlign, textStyle, hebrew = false, }: CmarkdownparagraphProps) {
    // Hebrew lines drop 200 weight units from the English equivalent (the
    // paragraph default is Ctext's Regular preset = Medium) and right-align,
    // unless the caller pinned an explicit alignment.
    const resolvedWeight = hebrew
        ? adjustWeightForHebrew(textWeight ?? FontWeight.Medium)
        : textWeight;
    const resolvedAlign = textAlign ?? (hebrew ? HEBREW_TEXT_ALIGN : undefined);
    const wrapperStyle: CSSProperties = {
        display: 'flex',
        ...(hebrew ? RTL_BLOCK_STYLE : null),
        ...style,
    };
    return (
      <div className={className} style={wrapperStyle}>
        <Ctext preset={TextPreset.Regular} size={textSize ?? 16} weight={resolvedWeight} align={resolvedAlign} style={{
            // Body-text typographic defaults live here on the paragraph wrapper
            // so inline tokens (text/strong/em/del/code/math/link) cascade from
            // it instead of each span hardcoding its own size + line-height.
            lineHeight: 1.65,
            letterSpacing: '0.3px',
            ...textStyle,
        }}>
          {children}
        </Ctext>
      </div>
    );
}
